import React from "react";
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  FileText,
  Minimize2,
  ChevronRight,
  Music,
  Disc3,
  Mic,
} from "lucide-react";
import { Waveform } from "./Waveform";

const fmt = (s) => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${ss.toString().padStart(2, "0")}`;
};

const fmtBpm = (n) => {
  if (n === null || n === undefined || n === "") return "";
  const num = parseFloat(n);
  if (isNaN(num)) return "";
  const [intP, decP = ""] = num.toFixed(3).split(".");
  return intP.padStart(3, "0") + "." + decP.padEnd(3, "0");
};

/**
 * Performance mode — full-screen, distraction-free, huge tap targets,
 * keeps screen awake via the Wake Lock API.
 * Sizes are calibrated at ~80% of the original to fit more info on stage screens.
 */
export const PerformanceMode = ({
  open,
  onClose,
  selected,
  playing,
  currentTime,
  duration,
  onTogglePlay,
  onPrev,
  onNext,
  onSeekFrac,
  onShowLyrics,
  nextInSetlist,
  onNextInSetlist,
  hasPdf,
  onSwitchVariant,
  hasRepete,
  hasPlayback,
  hasLive,
}) => {
  const wakeLockRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    const acquire = async () => {
      if (!("wakeLock" in navigator)) return;
      try {
        const wl = await navigator.wakeLock.request("screen");
        if (cancelled) {
          try { await wl.release(); } catch {}
          return;
        }
        wakeLockRef.current = wl;
        wl.addEventListener?.("release", () => { wakeLockRef.current = null; });
      } catch {
        // permission denied — ignore silently
      }
    };
    const release = async () => {
      const wl = wakeLockRef.current;
      wakeLockRef.current = null;
      if (wl) { try { await wl.release(); } catch {} }
    };
    if (open) {
      acquire();
      const onVis = () => {
        if (document.visibilityState === "visible" && open) acquire();
      };
      document.addEventListener("visibilitychange", onVis);
      return () => {
        cancelled = true;
        document.removeEventListener("visibilitychange", onVis);
        release();
      };
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === " ") { e.preventDefault(); onTogglePlay?.(); }
      else if (e.key === "ArrowLeft") onPrev?.();
      else if (e.key === "ArrowRight") onNext?.();
      else if (e.key === "Escape") onClose?.();
      else if (e.key.toLowerCase() === "n") onNextInSetlist?.();
      else if (e.key.toLowerCase() === "l") onShowLyrics?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onTogglePlay, onPrev, onNext, onClose, onNextInSetlist, onShowLyrics]);

  if (!open) return null;

  const progress = duration ? currentTime / duration : 0;

  return (
    <div
      className="fixed inset-0 z-[55] bg-black flex flex-col safe-bottom"
      data-testid="performance-mode"
    >
      {/* Top : ID + exit */}
      <div className="flex items-start justify-between px-5 pt-5 pb-2">
        <div className="font-display text-white text-5xl sm:text-6xl tabular-nums leading-none" data-testid="perf-id">
          {selected?.id || "—"}
        </div>
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-[#161616] border border-white/10 flex items-center justify-center hover:bg-white/5"
          data-testid="perf-close-btn"
          aria-label="Quitter le mode Performance"
        >
          <Minimize2 size={16} />
        </button>
      </div>

      {/* Title + composer */}
      <div className="px-5 pt-1 pb-2">
        <div
          className="text-[var(--gl-gold)] font-display text-2xl sm:text-4xl leading-tight break-words"
          data-testid="perf-title"
        >
          {selected?.title || "—"}
        </div>
        {(selected?.composer || selected?.artist) && (
          <div className="text-[var(--gl-green)] font-bold text-base sm:text-xl mt-1 break-words">
            {selected?.composer || selected?.artist}
          </div>
        )}
      </div>

      {/* Stage notes */}
      {selected?.notes ? (
        <div className="mx-5 mb-2 rounded-xl bg-[#0d0d0d] border-l-4 border-[var(--gl-gold)] border-y border-r border-white/10 px-3 py-2" data-testid="perf-notes">
          <div className="text-[9px] uppercase tracking-widest text-[var(--gl-gold)] font-bold mb-1">
            Notes de scène
          </div>
          <div className="text-white text-sm leading-snug whitespace-pre-wrap">
            {selected.notes}
          </div>
        </div>
      ) : null}

      {/* BPM + Ton */}
      <div className="px-5 grid grid-cols-2 gap-2">
        <div className="rounded-xl bg-[#0d0d0d] border border-white/10 px-3 py-2">
          <div className="text-[9px] uppercase tracking-widest text-neutral-400 font-bold">BPM</div>
          <div className="text-[var(--gl-gold)] font-display text-2xl tabular-nums">
            {fmtBpm(selected?.bpm) || "—"}
          </div>
        </div>
        <div className="rounded-xl bg-[#0d0d0d] border border-white/10 px-3 py-2">
          <div className="text-[9px] uppercase tracking-widest text-neutral-400 font-bold">Tonalité</div>
          <div className="text-[var(--gl-gold)] font-display text-2xl">
            {selected?.key || "—"}
          </div>
        </div>
      </div>

      {/* Audio variants (Répète / Playback / Live) */}
      {onSwitchVariant && (
        <div className="px-5 pt-2">
          <div className="grid grid-cols-3 gap-1.5">
            <button
              onClick={() => onSwitchVariant("repete")}
              disabled={!hasRepete}
              className={`rounded-lg px-2 py-2 text-[11px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 border transition disabled:opacity-30 disabled:cursor-not-allowed ${
                selected?.active_variant === "repete"
                  ? "bg-[var(--gl-gold)] text-black border-[var(--gl-gold)]"
                  : "bg-[#121212] text-white/70 border-white/10 hover:border-[var(--gl-gold)]/60"
              }`}
              data-testid="perf-variant-repete-btn"
            >
              <Music size={13} /> Répète
            </button>
            <button
              onClick={() => onSwitchVariant("playback")}
              disabled={!hasPlayback}
              className={`rounded-lg px-2 py-2 text-[11px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 border transition disabled:opacity-30 disabled:cursor-not-allowed ${
                selected?.active_variant === "playback" || !selected?.active_variant
                  ? "bg-[var(--gl-gold)] text-black border-[var(--gl-gold)]"
                  : "bg-[#121212] text-white/70 border-white/10 hover:border-[var(--gl-gold)]/60"
              }`}
              data-testid="perf-variant-playback-btn"
            >
              <Disc3 size={13} /> Playback
            </button>
            <button
              onClick={() => onSwitchVariant("live")}
              disabled={!hasLive}
              className={`rounded-lg px-2 py-2 text-[11px] font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 border transition disabled:opacity-30 disabled:cursor-not-allowed ${
                selected?.active_variant === "live"
                  ? "bg-red-500 text-black border-red-500"
                  : "bg-[#121212] text-red-400 border-red-500/30 hover:border-red-500/70"
              }`}
              data-testid="perf-variant-live-btn"
            >
              <Mic size={13} /> Live
            </button>
          </div>
        </div>
      )}

      {/* Waveform + time */}
      <div className="px-5 pt-3 flex items-center gap-2">
        <span className="text-[11px] text-neutral-400 w-9 tabular-nums">{fmt(currentTime)}</span>
        <Waveform seed={selected?.id || "A000"} progress={progress} onSeek={onSeekFrac} count={48} />
        <span className="text-[11px] text-neutral-400 w-9 tabular-nums text-right">{fmt(duration)}</span>
      </div>

      {/* Transport */}
      <div className="flex-1 px-5 py-4 flex items-center justify-center gap-4">
        <button
          onClick={onPrev}
          className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#161616] border border-white/10 flex items-center justify-center active:scale-95 transition"
          aria-label="Précédent"
          data-testid="perf-prev-btn"
        >
          <SkipBack size={26} />
        </button>
        <button
          onClick={onTogglePlay}
          className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-[var(--gl-gold)] text-black shadow-[0_0_48px_-10px_rgba(245,183,26,0.9)] flex items-center justify-center hover:brightness-110 active:scale-95 transition"
          aria-label={playing ? "Pause" : "Play"}
          data-testid="perf-play-btn"
        >
          {playing ? <Pause size={52} /> : <Play size={52} className="ml-1.5" />}
        </button>
        <button
          onClick={onNext}
          className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#161616] border border-white/10 flex items-center justify-center active:scale-95 transition"
          aria-label="Suivant"
          data-testid="perf-next-btn"
        >
          <SkipForward size={26} />
        </button>
      </div>

      {/* Bottom action row */}
      <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          onClick={onShowLyrics}
          disabled={!hasPdf}
          className="tap-lg rounded-xl bg-white text-black font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
          style={{ minHeight: 58 }}
          data-testid="perf-lyrics-btn"
        >
          <FileText size={18} /> Voir paroles
        </button>
        {nextInSetlist ? (
          <button
            onClick={onNextInSetlist}
            className="tap-lg rounded-xl bg-[var(--gl-gold)] text-black font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:brightness-110"
            style={{ minHeight: 58 }}
            data-testid="perf-setlist-next-btn"
          >
            <ChevronRight size={18} />
            Setlist · {nextInSetlist.id}
          </button>
        ) : (
          <button
            onClick={onNext}
            className="tap-lg rounded-xl bg-[#161616] border border-white/10 text-white font-bold uppercase tracking-wider flex items-center justify-center gap-2"
            style={{ minHeight: 58 }}
            data-testid="perf-skip-btn"
          >
            <SkipForward size={18} /> Suivant
          </button>
        )}
      </div>
    </div>
  );
};

export default PerformanceMode;
